import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import classNames from "classnames";
import dayjs from "dayjs";

import { CCard, CCardBody } from "@coreui/react";

import { comboApi } from "src/1/apis/combo.api";
import { comboItemApi } from "src/1/apis/combo_item.api";
import { CCheckbox, CInput, CTextarea } from "src/common/components/controls";
import { CTable } from "src/common/components/others";
import { WEEKDAYS_OPTIONS } from "src/modules/menu/constants";
import { money } from "src/utils/funcs";

const ChiTietDeXuatCombo = () => {
  //#region Data
  const params = useParams();

  const [selectedComboItem, setSelectedComboItem] = useState("");

  const { data, isFetching } = useQuery({
    queryKey: ["chi-tiet-de-xuat-combo", params?.id],
    queryFn: () => comboApi.getProposalById(params.id),
    enabled: Boolean(params.id),
    select: (response) => response?.data?.data,
  });

  useEffect(() => {
    if (data && data.items && data.items.length > 0) {
      setSelectedComboItem(data.items[0].id);
    }
  }, [data]);

  const { data: items = [], isFetching: isItemsFetching } = useQuery({
    queryKey: ["chi-tiet-mat-hang-trong-combo-item", selectedComboItem],
    queryFn: () => comboItemApi.getById(selectedComboItem),
    enabled: Boolean(selectedComboItem),
    select: (response) => response?.data?.data?.goods,
    staleTime: 0,
    cacheTime: 0,
  });
  //#endregion

  //#region Render
  const comboItemHeaders = [
    {
      key: "code",
      label: "Mã combo item",
      _style: { textAlign: "left" },
      sorter: false,
    },
    {
      key: "name",
      label: "Tên combo item",
      _style: { textAlign: "left" },
      sorter: false,
    },
    {
      key: "quantity",
      label: "Số lượng",
      _style: { width: 120 },
      sorter: false,
    },
    {
      key: "unit",
      label: "Đơn vị tính",
      sorter: false,
    },
  ];
  const comboItemRender = {
    code: ({ code, id }) => (
      <td
        className={classNames(
          "text-left",
          selectedComboItem === id && "text-[#0c35e1]"
        )}
        onClick={() => setSelectedComboItem(id)}
      >
        {code}
      </td>
    ),
    name: ({ name, id }) => (
      <td
        className={classNames(
          "text-left",
          selectedComboItem === id && "text-[#0c35e1]"
        )}
        onClick={() => setSelectedComboItem(id)}
      >
        {name}
      </td>
    ),
  };

  const itemHeaders = [
    {
      key: "code",
      label: "Mã mặt hàng",
      _style: { textAlign: "left" },
      sorter: false,
    },
    {
      key: "name",
      label: "Tên mặt hàng",
      _style: { textAlign: "left" },
      sorter: false,
    },
    {
      key: "unit",
      label: "Đơn vị tính",
      sorter: false,
    },
  ];
  const itemRender = {
    code: ({ code }) => <td className="text-left">{code}</td>,
    name: ({ name }) => <td className="text-left">{name}</td>,
  };

  const storesHeaders = [
    {
      key: "name",
      label: "Tên chi nhánh",
      sorter: false,
      _style: { textAlign: "left" },
    },
    {
      key: "price",
      label: "Giá",
      sorter: false,
    },
    {
      key: "from",
      label: "Từ",
      sorter: false,
    },
    {
      key: "to",
      label: "Đến",
      sorter: false,
    },
    {
      key: "holiday",
      label: "Áp dụng lễ",
      sorter: false,
    },
  ];
  const storesRender = {
    price: ({ price }) => <td>{money(price)}</td>,
    code: ({ code }) => <td className="text-left">{code}</td>,
    name: ({ name }) => <td className="text-left">{name}</td>,
    from: ({ from }) => (
      <td>{WEEKDAYS_OPTIONS.find((e) => e?.value === from)?.label}</td>
    ),
    to: ({ to }) => (
      <td>{WEEKDAYS_OPTIONS.find((e) => e?.value === to)?.label}</td>
    ),
    holiday: ({ holiday }) => (
      <td>
        <div className="flex items-center justify-center">
          <CCheckbox value={holiday} readOnly />
        </div>
      </td>
    ),
  };
  return (
    <div className="flex flex-col gap-4">
      <CCard>
        <CCardBody>
          <p className="text-lg font-semibold">
            Thông tin chi tiết đề xuất Combo
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
            <div className="lg:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-2">
              <CInput label="Mã Combo" value={data?.code} disabled />
              <CInput label="Tên Combo" value={data?.name} disabled />
              <CInput
                label="Áp dụng từ ngày"
                value={data?.from ? dayjs(data.from).format("DD/MM/YYYY") : ""}
                disabled
              />
              <CInput
                label="Đến ngày"
                value={data?.to ? dayjs(data.to).format("DD/MM/YYYY") : ""}
                disabled
              />
              <CTextarea label="Ghi chú" value={data?.note} disabled />
            </div>
            <div>
              <img src={data?.file?.path} alt="" />
            </div>
          </div>
        </CCardBody>
      </CCard>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <CCard>
          <CCardBody>
            <p className="text-lg font-semibold">Danh sách Combo item</p>
            <CTable
              loading={isFetching}
              fields={comboItemHeaders}
              render={comboItemRender}
              data={data?.items ?? []}
              itemsPerPage={999999}
            />

            <p className="font-semibold m-0 mt-4 text-base">
              Tổng cost từ combo item:&nbsp;
              <span className="text-[#0c35e1]">
                {(data?.cost ?? 0)?.toLocaleString()}
                &nbsp; VNĐ
              </span>
            </p>
          </CCardBody>
        </CCard>
        <CCard>
          <CCardBody>
            <p className="text-lg font-semibold">
              Các mặt hàng trong Combo Item:&nbsp;
              {data?.items?.find((e) => e.id === selectedComboItem)?.name}
            </p>
            <CTable
              loading={isItemsFetching}
              fields={itemHeaders}
              render={itemRender}
              data={items ?? []}
              itemsPerPage={999999}
            />
          </CCardBody>
        </CCard>
      </div>
      <CCard>
        <CCardBody>
          <p className="text-lg font-semibold">
            Chi tiết giá bán tại chi nhánh
          </p>
          <div className="max-h-[360px] overflow-auto">
            <CTable
              fields={storesHeaders}
              render={storesRender}
              data={data?.stores ?? []}
              itemsPerPage={999999}
            />
          </div>
        </CCardBody>
      </CCard>
    </div>
  );
  //#endregion
};
export default ChiTietDeXuatCombo;
